import React, { useState, useRef, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import ElementsDataService from '../../api/entitiesDataServices/ElementsDataService.js'
import AuthenticationService from '../general/AuthenticationService.js'

const T = {
    bg:'#f1f5f9', card:'#ffffff', border:'#e8edf5',
    blue:'#3b82f6', navy:'#0f172a',
    txt:'#1e293b', txt2:'#64748b', muted:'#94a3b8',
    green:'#16a34a', greenBg:'#f0fdf4', greenBdr:'#bbf7d0',
    red:'#dc2626', redBg:'#fef2f2', redBdr:'#fecaca',
    warn:'#f59e0b', warnBg:'#fffbeb', warnBdr:'#fde68a',
    r:16, rsm:10,
    shadow:'0 1px 3px rgba(15,23,42,.06)',
    font:"'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
}

const STEPS = [
    { emoji:'📤', label:'Carga del PDF',           desc:'Enviando archivo al servidor…'          },
    { emoji:'🔍', label:'Descifrando contenido',   desc:'Analizando estructura del documento…'   },
    { emoji:'💾', label:'Cargando contenido',      desc:'Guardando declaración en el sistema…'   },
    { emoji:'✅', label:'¡Éxito!',                 desc:'Declaración registrada correctamente.'  },
]

function Stepper({ step, error }) {
    return (
        <div style={{ display:'flex', flexDirection:'column' }}>
            {STEPS.map((s, i) => {
                const done    = step > i
                const active  = step === i && !error
                const failed  = step === i && error
                const pending = step < i
                const last    = i === STEPS.length - 1

                return (
                    <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:14 }}>
                        {/* Circle + connector */}
                        <div style={{ display:'flex', flexDirection:'column', alignItems:'center', flexShrink:0 }}>
                            <div style={{
                                width:40, height:40, borderRadius:'50%',
                                background: done    ? T.green
                                          : active  ? T.blue
                                          : failed  ? T.red
                                          : '#e2e8f0',
                                display:'flex', alignItems:'center', justifyContent:'center',
                                fontSize: done ? 15 : 18,
                                color: (done || active || failed) ? 'white' : '#94a3b8',
                                transition:'all .4s ease',
                                boxShadow: active ? `0 0 0 5px ${T.blue}20` : 'none',
                                flexShrink:0,
                            }}>
                                {done ? '✓'
                                : active ? (
                                    <span style={{
                                        width:15, height:15, borderRadius:'50%',
                                        border:'2.5px solid rgba(255,255,255,.35)',
                                        borderTopColor:'white',
                                        display:'inline-block',
                                        animation:'czSpin .7s linear infinite',
                                    }}/>
                                )
                                : failed ? '✕'
                                : s.emoji}
                            </div>
                            {!last && (
                                <div style={{
                                    width:2, height:28, marginTop:2,
                                    background: done ? T.green : '#e2e8f0',
                                    transition:'background .4s ease',
                                }}/>
                            )}
                        </div>

                        {/* Text */}
                        <div style={{ paddingTop:8, paddingBottom: last ? 0 : 30 }}>
                            <div style={{
                                fontSize:14, fontWeight:700,
                                color: done ? T.green : active ? T.navy : failed ? T.red : T.muted,
                                transition:'color .3s',
                            }}>
                                {s.label}
                            </div>
                            {(active || done || failed) && (
                                <div style={{
                                    fontSize:12, marginTop:2,
                                    color: failed ? T.red : T.txt2,
                                    animation:'czFadeIn .25s ease',
                                }}>
                                    {failed ? 'Error al procesar el archivo.' : s.desc}
                                </div>
                            )}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

function saveFile(formData) {
    const config = { headers: { 'Content-Type': 'multipart/form-data' } }
    return ElementsDataService.createElement('cfdis/declaracion', formData, config)
}
const pause = ms => new Promise(r => setTimeout(r, ms))

export default function DeclaracionProvisionalComponent() {
    const history = useHistory()

    const [files,      setFiles]      = useState([])
    const [results,    setResults]    = useState([])
    const [dragOver,   setDragOver]   = useState(false)
    const [uploading,  setUploading]  = useState(false)
    const [step,       setStep]       = useState(-1)
    const [stepError,  setStepError]  = useState(false)
    const [activeFile, setActiveFile] = useState(null)
    const inputRef = useRef(null)

    useEffect(() => {
        if (!AuthenticationService.haveAuthorization('WRITE_CFDIS')) {
            history.push('/login')
        }
    }, []) // eslint-disable-line

    const addFiles = incoming => {
        setFiles(prev => {
            const ex = new Set(prev.map(f => f.name))
            return [...prev, ...incoming.filter(f => !ex.has(f.name))]
        })
        setResults([])
        setStep(-1)
        setStepError(false)
    }

    const handleFiles = e => addFiles(Array.from(e.target.files || []))
    const handleDrop  = e => {
        e.preventDefault(); setDragOver(false)
        addFiles(Array.from(e.dataTransfer.files))
    }
    const removeFile = name => setFiles(f => f.filter(x => x.name !== name))

    const handleSubmit = async () => {
        if (!files.length) return
        setUploading(true)
        setResults([])
        const acc = []

        for (let i = 0; i < files.length; i++) {
            const file = files[i]
            setActiveFile(file)
            setStepError(false)
            setStep(0); await pause(320)
            setStep(1); await pause(480)
            setStep(2)

            let ok = false
            try {
                const fd = new FormData()
                fd.append('declaracionProvisionalFile', file)
                const res = await saveFile(fd)
                ok = res?.data?.message === 'Created'
            } catch (_) { ok = false }

            if (ok) {
                setStep(3); await pause(500)
            } else {
                setStepError(true); await pause(600)
            }

            acc.push({ name: file.name, size: file.size, ok })
            setResults([...acc])
        }

        setStep(4)
        setActiveFile(null)
        setUploading(false)
        if (acc.every(r => r.ok)) setFiles([])
    }

    const done         = step === 4 && results.length > 0
    const successCount = results.filter(r => r.ok).length
    const failCount    = results.filter(r => !r.ok).length
    const idle         = !uploading && step < 0

    return (
        <div style={{ minHeight:'100vh', background:T.bg, fontFamily:T.font, padding:'28px 24px 80px' }}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
                @keyframes czSpin    { to { transform:rotate(360deg); } }
                @keyframes czFadeIn  { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:none; } }
                @keyframes czSlideIn { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:none; } }
            `}</style>

            <div style={{ maxWidth:580, margin:'0 auto' }}>

                {/* ── Header ── */}
                <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:24 }}>
                    <button onClick={() => history.goBack()}
                        style={{ width:36, height:36, borderRadius:10, border:`1px solid ${T.border}`,
                            background:T.card, cursor:'pointer', fontSize:17, color:T.txt2,
                            display:'flex', alignItems:'center', justifyContent:'center',
                            boxShadow:T.shadow, flexShrink:0, fontFamily:T.font, transition:'all .15s' }}
                        onMouseEnter={e => { e.currentTarget.style.background=T.navy; e.currentTarget.style.color='white' }}
                        onMouseLeave={e => { e.currentTarget.style.background=T.card; e.currentTarget.style.color=T.txt2 }}>
                        ‹
                    </button>
                    <div>
                        <h1 style={{ margin:0, fontSize:21, fontWeight:800, color:T.navy, letterSpacing:'-.025em' }}>
                            Cargar Declaraciones Provisionales
                        </h1>
                        <p style={{ margin:'2px 0 0', fontSize:13, color:T.txt2 }}>
                            Sube uno o varios archivos PDF del SAT
                        </p>
                    </div>
                </div>

                {/* ── Main card ── */}
                <div style={{ background:T.card, border:`1px solid ${T.border}`, borderRadius:T.r, boxShadow:T.shadow, overflow:'hidden' }}>

                    {/* DROP ZONE — only while idle */}
                    {idle && (
                        <div
                            onClick={() => inputRef.current?.click()}
                            onDragOver={e => { e.preventDefault(); setDragOver(true) }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={handleDrop}
                            style={{
                                margin:20, borderRadius:12,
                                border:`2px dashed ${dragOver ? T.blue : T.border}`,
                                background: dragOver ? `${T.blue}07` : T.bg,
                                padding:'38px 20px', textAlign:'center',
                                cursor:'pointer', transition:'all .2s',
                                boxShadow: dragOver ? `0 0 0 4px ${T.blue}15` : 'none',
                            }}>
                            <div style={{ fontSize:38, marginBottom:10 }}>📎</div>
                            <p style={{ margin:0, fontSize:15, fontWeight:600, color:T.txt }}>
                                Arrastra archivos aquí o haz clic para seleccionar
                            </p>
                            <p style={{ margin:'5px 0 0', fontSize:12, color:T.muted }}>
                                Múltiples archivos · Solo PDF
                            </p>
                            <input ref={inputRef} type="file" multiple
                                onChange={handleFiles} style={{ display:'none' }} />
                        </div>
                    )}

                    {/* FILE LIST — idle, files selected */}
                    {idle && files.length > 0 && (
                        <div style={{ margin:'0 20px 16px' }}>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                                <span style={{ fontSize:13, fontWeight:700, color:T.txt }}>
                                    {files.length} archivo{files.length !== 1 ? 's' : ''} seleccionado{files.length !== 1 ? 's' : ''}
                                </span>
                                <button onClick={() => setFiles([])}
                                    style={{ fontSize:12, color:T.muted, background:'none', border:'none', cursor:'pointer', fontFamily:T.font }}>
                                    Limpiar todo
                                </button>
                            </div>
                            {files.map(f => (
                                <div key={f.name} style={{
                                    display:'flex', alignItems:'center', gap:10,
                                    padding:'8px 12px', borderRadius:8, marginBottom:4,
                                    background:T.bg, border:`1px solid ${T.border}`,
                                }}>
                                    <span style={{ fontSize:15 }}>📄</span>
                                    <span style={{ flex:1, fontSize:13, color:T.txt, fontWeight:500,
                                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                        {f.name}
                                    </span>
                                    <span style={{ fontSize:11, color:T.muted, flexShrink:0 }}>
                                        {(f.size/1024).toFixed(0)} KB
                                    </span>
                                    <button onClick={() => removeFile(f.name)}
                                        style={{ background:'none', border:'none', cursor:'pointer',
                                            color:T.muted, fontSize:17, lineHeight:1, padding:'0 2px', fontFamily:T.font }}>
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* SUBMIT BUTTON — idle */}
                    {idle && (
                        <div style={{ padding:'0 20px 20px' }}>
                            <button
                                onClick={handleSubmit}
                                disabled={files.length === 0}
                                style={{
                                    width:'100%', padding:'13px', borderRadius:T.rsm,
                                    background: files.length === 0 ? '#cbd5e1' : T.blue,
                                    border:'none', color:'white', fontSize:14, fontWeight:700,
                                    cursor: files.length === 0 ? 'not-allowed' : 'pointer',
                                    fontFamily:T.font, transition:'all .15s',
                                    boxShadow: files.length > 0 ? `0 4px 14px ${T.blue}40` : 'none',
                                }}>
                                ✅ Procesar {files.length > 0 ? `(${files.length} archivo${files.length !== 1 ? 's' : ''})` : ''}
                            </button>
                        </div>
                    )}

                    {/* ── PROGRESS STEPPER — while uploading ── */}
                    {uploading && (
                        <div style={{ padding:'28px 32px', animation:'czSlideIn .3s ease' }}>
                            {/* File counter badge */}
                            <div style={{
                                display:'inline-flex', alignItems:'center', gap:6,
                                padding:'4px 10px', borderRadius:20,
                                background:`${T.blue}12`, marginBottom:14,
                            }}>
                                <span style={{ width:6, height:6, borderRadius:'50%', background:T.blue,
                                    animation:'czSpin 1.2s linear infinite', display:'inline-block' }}/>
                                <span style={{ fontSize:12, fontWeight:600, color:T.blue }}>
                                    Procesando {results.length + 1} de {files.length}
                                </span>
                            </div>

                            {/* Active file name */}
                            <div style={{ marginBottom:20 }}>
                                <div style={{ fontSize:14, fontWeight:700, color:T.txt,
                                    overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                    {activeFile?.name}
                                </div>
                                <div style={{ fontSize:12, color:T.muted, marginTop:2 }}>
                                    {activeFile ? `${(activeFile.size/1024).toFixed(0)} KB` : ''}
                                </div>
                            </div>

                            <Stepper step={step} error={stepError} />
                        </div>
                    )}

                    {/* ── SUMMARY — when done ── */}
                    {done && (
                        <div style={{ padding:'28px 32px', animation:'czSlideIn .4s ease' }}>

                            {/* Banner */}
                            <div style={{
                                textAlign:'center', padding:'20px 16px 18px',
                                borderRadius:12, marginBottom:20,
                                background: failCount === 0 ? T.greenBg : T.warnBg,
                                border:`1px solid ${failCount === 0 ? T.greenBdr : T.warnBdr}`,
                            }}>
                                <div style={{ fontSize:34, marginBottom:6 }}>
                                    {failCount === 0 ? '🎉' : '⚠️'}
                                </div>
                                <div style={{ fontSize:16, fontWeight:800, letterSpacing:'-.015em',
                                    color: failCount === 0 ? T.green : T.warn, marginBottom:4 }}>
                                    {failCount === 0 ? '¡Carga completada con éxito!' : 'Proceso finalizado con errores'}
                                </div>
                                <div style={{ fontSize:13, color:T.txt2 }}>
                                    {failCount === 0
                                        ? `${successCount} declaración${successCount !== 1 ? 'es' : ''} registrada${successCount !== 1 ? 's' : ''} correctamente`
                                        : `${successCount} exitosa${successCount !== 1 ? 's' : ''} · ${failCount} con error`}
                                </div>
                            </div>

                            {/* Per-file results */}
                            <div style={{ marginBottom:20 }}>
                                {results.map((r, i) => (
                                    <div key={i} style={{
                                        display:'flex', alignItems:'center', gap:10,
                                        padding:'10px 14px', borderRadius:9, marginBottom:6,
                                        background: r.ok ? T.greenBg : T.redBg,
                                        border:`1px solid ${r.ok ? T.greenBdr : T.redBdr}`,
                                        animation:`czFadeIn .3s ease ${i * 80}ms both`,
                                    }}>
                                        <span style={{ fontSize:17, flexShrink:0 }}>{r.ok ? '✅' : '❌'}</span>
                                        <div style={{ flex:1, minWidth:0 }}>
                                            <div style={{ fontSize:13, fontWeight:600, color:T.txt,
                                                overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                                {r.name}
                                            </div>
                                            <div style={{ fontSize:11, color:T.txt2, marginTop:1 }}>
                                                {r.ok
                                                    ? 'Declaración provisional registrada en el sistema'
                                                    : 'No se pudo procesar — verifica el archivo PDF'}
                                                {' · '}{(r.size/1024).toFixed(0)} KB
                                            </div>
                                        </div>
                                        <span style={{ fontSize:12, fontWeight:700, flexShrink:0,
                                            color: r.ok ? T.green : T.red }}>
                                            {r.ok ? 'Exitoso' : 'Error'}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            {/* Actions */}
                            <div style={{ display:'flex', gap:10 }}>
                                <button
                                    onClick={() => { setResults([]); setStep(-1); setFiles([]) }}
                                    style={{ flex:1, padding:'12px', borderRadius:T.rsm,
                                        background:T.blue, border:'none', color:'white',
                                        fontSize:13, fontWeight:700, cursor:'pointer',
                                        fontFamily:T.font, boxShadow:`0 4px 14px ${T.blue}40` }}>
                                    + Cargar más archivos
                                </button>
                                <button
                                    onClick={() => history.goBack()}
                                    style={{ padding:'12px 18px', borderRadius:T.rsm,
                                        background:'transparent', border:`1px solid ${T.border}`,
                                        color:T.txt2, fontSize:13, fontWeight:600,
                                        cursor:'pointer', fontFamily:T.font }}>
                                    Volver
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Signature ── */}
                <div style={{ textAlign:'center', marginTop:32, fontSize:12, color:T.muted, letterSpacing:'.01em' }}>
                    © 2026 Cloud Zenyth by KrvheD
                </div>

            </div>
        </div>
    )
}
